import { useState, useEffect, useCallback, useRef } from 'react';
import { FiPlus, FiX } from 'react-icons/fi';
import { getVersions, getChapter } from '../../utils/bible-api';
import StrongDictionaryAdvanced from './StrongDictionaryAdvanced';

/**
 * Componente compacto de lectura bíblica para referencias específicas
 * Optimizado para mostrar un capítulo con foco en versículos específicos
 * Incluye soporte para múltiples paneles (hasta 2 biblias en paralelo)
 */
export default function BibleReaderCompact({ 
  versionId = 1, 
  bookName, 
  chapter, 
  focusVerses = [], // Array de números de versículos para resaltar
  maxHeight = '350px',
  showStrongs = true 
}) {
  const [versions, setVersions] = useState([]);
  const [panels, setPanels] = useState([
    { id: 1, versionId, book: bookName, chapter, verses: [], loading: false }
  ]);
  const [selectedStrong, setSelectedStrong] = useState(null);
  const [showDictionary, setShowDictionary] = useState(false);
  const verseRefs = useRef({});
  
  // Estados para controlar visualización de componentes Strong's
  const [displayOptions, setDisplayOptions] = useState({
    showStrongNumber: true,
    showHebrewText: true,
    showTransliteration: true,
    showLexeme: false,
    showMorpheme: true,
    showPhoneme: false,
    showDerivativeMorphemes: false,
    showFlexiveMorphemes: false
  });

  // Cargar versiones disponibles
  useEffect(() => {
    const loadVersions = async () => {
      try {
        const response = await getVersions();
        setVersions(response.data || []);
      } catch (error) {
        console.error('Error al cargar versiones:', error);
      }
    };
    loadVersions();
  }, []);

  // Actualizar panel
  const updatePanel = useCallback((panelId, updates) => {
    setPanels(prevPanels => 
      prevPanels.map(panel => 
        panel.id === panelId ? { ...panel, ...updates } : panel
      )
    );
  }, []);

  // Cargar capítulo para un panel
  const loadChapterForPanel = useCallback(async (panelId) => {
    setPanels(currentPanels => {
      const panel = currentPanels.find(p => p.id === panelId);
      if (!panel || !panel.versionId || !panel.book || !panel.chapter) {
        console.log('Missing panel data:', { panel, panelId });
        return currentPanels;
      }

      if (panel.loading) {
        console.log('Panel already loading, skipping:', panelId);
        return currentPanels;
      }

      // Marcar como loading inmediatamente
      const updatedPanels = currentPanels.map(p => 
        p.id === panelId ? { ...p, loading: true } : p
      );

      // Cargar los datos de forma asíncrona
      (async () => {
        try {
          console.log('Loading chapter for panel:', { 
            panelId, 
            versionId: panel.versionId, 
            book: panel.book, 
            chapter: panel.chapter 
          });
          const response = await getChapter(panel.versionId, panel.book, panel.chapter);
          console.log('Chapter response for panel', panelId, ':', response);
          
          if (response && response.data && response.data.verses) {
            console.log('Verses loaded for panel', panelId, ':', response.data.verses.length);
            console.log('First verse structure:', response.data.verses[0]);
            console.log('Has strongs array?', !!response.data.verses[0]?.strongs);
            console.log('Strongs length:', response.data.verses[0]?.strongs?.length || 0);
            
            // Verificar si los Strong's vienen en el array de versículos
            const hasStrongsInResponse = response.data.verses.some(v => v.strongs && v.strongs.length > 0);
            console.log('Panel', panelId, 'has strongs in response:', hasStrongsInResponse);
            
            setPanels(prev => prev.map(p => 
              p.id === panelId 
                ? { ...p, verses: response.data.verses, loading: false } 
                : p
            ));
          } else {
            console.error('Invalid response format for panel', panelId, ':', response);
            setPanels(prev => prev.map(p => 
              p.id === panelId ? { ...p, verses: [], loading: false } : p
            ));
          }
        } catch (error) {
          console.error('Error al cargar capítulo para panel', panelId, ':', error);
          setPanels(prev => prev.map(p => 
            p.id === panelId ? { ...p, verses: [], loading: false } : p
          ));
        }
      })();

      return updatedPanels;
    });
  }, []);

  // Cargar capítulo inicial (solo una vez al montar)
  const initialLoadRef = useRef(false);
  useEffect(() => {
    if (bookName && chapter && versionId && !initialLoadRef.current) {
      initialLoadRef.current = true;
      loadChapterForPanel(1);
    }
  }, [bookName, chapter, versionId, loadChapterForPanel]);

  // Scroll automático al versículo enfocado cuando se carga
  useEffect(() => {
    const firstPanel = panels[0];
    if (firstPanel && firstPanel.verses.length > 0 && focusVerses.length > 0) {
      setTimeout(() => {
        const firstFocusVerse = focusVerses[0];
        const verseElement = verseRefs.current[`panel-1-verse-${firstFocusVerse}`];
        if (verseElement) {
          verseElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        }
      }, 100);
    }
  }, [panels, focusVerses]);

  const handleStrongClick = useCallback((strongNumber, strongData) => {
    setSelectedStrong({ number: strongNumber, ...strongData });
    setShowDictionary(true);
  }, []);

  const toggleDisplayOption = (option) => {
    setDisplayOptions(prev => ({
      ...prev,
      [option]: !prev[option]
    }));
  };

  // Agregar nuevo panel
  const addPanel = () => {
    if (panels.length >= 2) return; // Máximo 2 paneles

    const newId = Math.max(...panels.map(p => p.id)) + 1;
    const newPanel = {
      id: newId,
      versionId: versions.find(v => v.id !== panels[0].versionId)?.id || versionId,
      book: bookName,
      chapter: chapter,
      verses: [],
      loading: false
    };
    setPanels([...panels, newPanel]);
    
    // Cargar capítulo para el nuevo panel
    setTimeout(() => loadChapterForPanel(newId), 100);
  };

  // Eliminar panel
  const removePanel = (panelId) => {
    if (panels.length === 1) return; // No eliminar el último panel
    setPanels(panels.filter(p => p.id !== panelId));
  };

  // Cambiar versión de un panel
  const changeVersion = (panelId, newVersionId) => {
    updatePanel(panelId, { versionId: parseInt(newVersionId) });
    setTimeout(() => loadChapterForPanel(panelId), 100);
  };

  // Verificar si el versículo debe estar resaltado
  const isVerseHighlighted = (verseNumber) => {
    return focusVerses.includes(verseNumber);
  };

  // Renderizar un panel de biblia
  const renderPanel = (panel) => {
    const hasStrongs = panel.verses.some(v => v.strongs && v.strongs.length > 0);
    const currentVersion = versions.find(v => v.id === panel.versionId);

    console.log('Panel', panel.id, 'hasStrongs:', hasStrongs, 'verses:', panel.verses.length);

    return (
      <div key={panel.id} className="flex-1 min-w-0">
        {/* Header del panel */}
        <div className="flex items-center justify-between mb-2 p-2 bg-gray-100 rounded-lg">
          <select
            value={panel.versionId}
            onChange={(e) => changeVersion(panel.id, e.target.value)}
            className="text-sm border border-gray-300 rounded px-2 py-1 bg-white flex-1"
            disabled={panel.loading}
          >
            {versions.map(v => (
              <option key={v.id} value={v.id}>
                {v.abbreviation || v.name}
              </option>
            ))}
          </select>
          
          {panels.length > 1 && (
            <button
              onClick={() => removePanel(panel.id)}
              className="ml-2 p-1 hover:bg-red-100 rounded text-red-600"
              title="Cerrar panel"
            >
              <FiX className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Contenido del panel */}
        {panel.loading ? (
          <div className="flex items-center justify-center p-8">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <p className="text-xs text-gray-600 mt-2">Cargando...</p>
            </div>
          </div>
        ) : panel.verses.length === 0 ? (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-xs text-amber-700">
              No se encontraron versículos para este capítulo.
            </p>
          </div>
        ) : (
          <div 
            className="overflow-y-auto bg-white rounded-lg border border-gray-300 p-3"
            style={{ maxHeight: showDictionary ? '300px' : maxHeight }}
          >
            {panel.verses.map((verse) => {
              const isHighlighted = isVerseHighlighted(verse.verseNumber);
              
              // Extraer palabras del texto limpio
              const words = verse.text ? verse.text.split(/\s+/) : [];
              
              console.log(`Verse ${verse.verseNumber}:`, {
                text: verse.text?.substring(0, 50),
                wordsCount: words.length,
                strongsCount: verse.strongs?.length || 0
              });
              
              return (
                <div
                  key={verse.verseNumber}
                  ref={(el) => (verseRefs.current[`panel-${panel.id}-verse-${verse.verseNumber}`] = el)}
                  className={`mb-3 ${
                    isHighlighted
                      ? 'bg-yellow-100 border-l-4 border-yellow-500 pl-3 py-2 -ml-2'
                      : ''
                  }`}
                  style={{ display: 'block' }}
                >
                  {/* Contenedor flex para número + contenido */}
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    {/* Número del versículo - SIEMPRE visible con estilos inline */}
                    <span 
                      style={{
                        fontWeight: 'bold',
                        color: '#2563eb',
                        fontSize: '16px',
                        flexShrink: 0,
                        minWidth: '24px',
                        userSelect: 'none',
                        display: 'inline-block'
                      }}
                    >
                      {verse.verseNumber}
                    </span>
                    
                    {/* Contenido del versículo */}
                    <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'flex-start' }}>
                      {/* Si tiene Strong's, mostrar con interactividad */}
                      {hasStrongs && showStrongs && verse.strongs && verse.strongs.length > 0 ? (
                        verse.strongs.map((strong, idx) => {
                          // Intentar obtener la palabra correspondiente del texto
                          const word = words[idx] || '';
                          
                          return (
                            <span
                              key={`${verse.verseNumber}-${idx}`}
                              className="inline-flex flex-col items-start relative group cursor-pointer"
                              onClick={() => handleStrongClick(strong.strong, strong)}
                            >
                              {/* Texto Hebreo arriba */}
                              {displayOptions.showHebrewText && strong.hebrewText && (
                                <span className="text-[10px] text-blue-600 font-hebrew leading-none" dir="rtl">
                                  {strong.hebrewText.trim()}
                                </span>
                              )}

                              {/* Palabra en español (más grande) */}
                              <span className="hover:bg-blue-100 px-0.5 rounded text-sm">
                                {word}
                              </span>

                              {/* Información debajo: Strong + Transliteración + Morfema en una línea */}
                              <span className="flex items-center gap-0.5 text-[9px] leading-none">
                                {/* Número Strong */}
                                {displayOptions.showStrongNumber && (
                                  <span className="text-blue-600 font-mono font-semibold">
                                    {strong.strong}
                                  </span>
                                )}

                                {/* Transliteración */}
                                {displayOptions.showTransliteration && strong.transliteration && (
                                  <span className="text-gray-500 italic">
                                    {strong.transliteration}
                                  </span>
                                )}

                                {/* Morfema */}
                                {displayOptions.showMorpheme && strong.morphology && (
                                  <span className="text-purple-600 font-mono">
                                    {strong.morphology}
                                  </span>
                                )}
                              </span>

                              {/* Tooltip en hover */}
                              <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block z-10">
                                <div className="bg-gray-900 text-white text-xs rounded-lg p-2 shadow-lg w-48">
                                  <div className="font-semibold mb-1">{strong.strong}</div>
                                  {strong.hebrewText && (
                                    <div className="text-gray-300 font-hebrew mb-1" dir="rtl">
                                      {strong.hebrewText}
                                    </div>
                                  )}
                                  {strong.transliteration && (
                                    <div className="text-gray-400 italic">{strong.transliteration}</div>
                                  )}
                                  <div className="text-gray-400 mt-1 text-[10px]">
                                    Haz clic para ver diccionario completo
                                  </div>
                                </div>
                              </div>
                            </span>
                          );
                        })
                      ) : (
                        <span className="text-sm">{verse.text}</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const hasStrongs = panels.some(panel => 
    panel.verses && panel.verses.some(v => v.strongs && v.strongs.length > 0)
  );

  console.log('Global hasStrongs:', hasStrongs);

  return (
    <div className="relative">
      {/* Controles de visualización y agregar panel */}
      <div className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold text-gray-700">Controles:</p>
          {panels.length < 2 && versions.length > 1 && (
            <button
              onClick={addPanel}
              className="flex items-center gap-1 px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
            >
              <FiPlus className="w-3 h-3" />
              Agregar Biblia
            </button>
          )}
        </div>
        
        {hasStrongs && showStrongs && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => toggleDisplayOption('showStrongNumber')}
              className={`px-2 py-1 text-xs rounded ${
                displayOptions.showStrongNumber
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700'
              }`}
            >
              Número Strong
            </button>
            <button
              onClick={() => toggleDisplayOption('showHebrewText')}
              className={`px-2 py-1 text-xs rounded ${
                displayOptions.showHebrewText
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700'
              }`}
            >
              Texto Hebreo
            </button>
            <button
              onClick={() => toggleDisplayOption('showTransliteration')}
              className={`px-2 py-1 text-xs rounded ${
                displayOptions.showTransliteration
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700'
              }`}
            >
              Transliteración
            </button>
            <button
              onClick={() => toggleDisplayOption('showMorpheme')}
              className={`px-2 py-1 text-xs rounded ${
                displayOptions.showMorpheme
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700'
              }`}
            >
              Morfema
            </button>
          </div>
        )}
      </div>

      {/* Paneles de biblias */}
      <div className={`flex gap-3 ${panels.length > 1 ? 'grid grid-cols-2' : ''}`}>
        {panels.map(panel => renderPanel(panel))}
      </div>

      {/* Diccionario Strong's */}
      {showDictionary && selectedStrong && (
        <div className="mt-4 max-h-96 overflow-y-auto">
          <StrongDictionaryAdvanced
            strongNumber={selectedStrong.number}
            onClose={() => {
              setShowDictionary(false);
              setSelectedStrong(null);
            }}
          />
        </div>
      )}
    </div>
  );
}
